import React from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  HStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';

/**
 * Modal component for displaying JurisConsult data
 */
const JurisConsultModal = ({ isOpen, onClose, data, numeroPrecatorio, loading }) => {
  // Color mode values
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const highlightColor = useColorModeValue('brand.primary', 'brand.secondary');
  const cardBg = useColorModeValue('white', 'gray.800');

  // Function to format date string
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  // Function to render key-value info fields
  const InfoField = ({ label, value }) => (
    <Box mb={2}>
      <Text fontSize="sm" color={textColor} fontWeight="medium">{label}</Text>
      <Text fontSize="md">{value || '-'}</Text>
    </Box>
  );

  // Function to get badge color based on polo
  const getPoloColor = (polo) => {
    if (polo === 'ATIVO') return 'green';
    if (polo === 'PASSIVO') return 'red';
    if (polo === 'ADVOGADO') return 'purple';
    if (polo === 'DESCONHECIDO') return 'gray';
    return 'blue';
  };

  // Get jurisConsultData from the response
  const jurisData = data?.jurisConsultData || {};
  const fontes = jurisData.fontes || [];
  const oabs = data?.oabs || [];
  const documentos = data?.documentos || {};
  const processosRelacionados = jurisData.processos_relacionados || [];

  // Extract all envolvidos from fontes
  const getAllEnvolvidos = () => {
    const envolvidos = [];
    fontes.forEach(fonte => {
      if (fonte.envolvidos) {
        fonte.envolvidos.forEach(env => {
          envolvidos.push({ ...env, fonte_nome: fonte.nome || fonte.sigla });
        });
      }
    });
    return envolvidos;
  };

  const allEnvolvidos = getAllEnvolvidos();

  // Format CPF
  const formatCPF = (cpf) => {
    if (!cpf) return null;
    const cleaned = cpf.replace(/\D/g, '');
    if (cleaned.length !== 11) return cpf;
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  // Format CNPJ
  const formatCNPJ = (cnpj) => {
    if (!cnpj) return null;
    const cleaned = cnpj.replace(/\D/g, '');
    if (cleaned.length !== 14) return cnpj;
    return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg={tableHeaderBg} borderTopRadius="md">
          <Flex align="center" justify="space-between">
            <Text>Dados JurisConsult</Text>
            <Badge colorScheme="blue" fontSize="sm">
              {data?.numeroPrecatorio || numeroPrecatorio}
            </Badge>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pt={4}>
          {loading ? (
            <VStack spacing={4} py={8}>
              <Spinner size="xl" color={highlightColor} thickness="4px" />
              <Text>Carregando dados...</Text>
              <Text fontSize="sm" color={textColor}>
                Esta operação pode levar algum tempo...
              </Text>
            </VStack>
          ) : !data ? (
            <Alert status="info" variant="left-accent">
              <AlertIcon />
              <VStack align="start" spacing={2}>
                <Text>Dados do JurisConsult não disponíveis para este precatório.</Text>
              </VStack>
            </Alert>
          ) : (
            <Box>
              <Tabs colorScheme="blue" variant="enclosed" mt={2}>
                <TabList>
                  <Tab>Informações Básicas</Tab>
                  {fontes.length > 0 && (
                    <Tab>Fontes ({fontes.length})</Tab>
                  )}
                  {allEnvolvidos.length > 0 && (
                    <Tab>Envolvidos ({allEnvolvidos.length})</Tab>
                  )}
                  {oabs.length > 0 && (
                    <Tab>OABs ({oabs.length})</Tab>
                  )}
                </TabList>

                <TabPanels>
                  {/* Tab 1: Informações Básicas */}
                  <TabPanel>
                    <Card variant="outline" borderColor={borderColor} bg={cardBg} mb={4}>
                      <CardHeader pb={2}>
                        <Heading size="md">Dados do Processo</Heading>
                      </CardHeader>
                      <Divider />
                      <CardBody>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                          <Box>
                            <InfoField
                              label="Número Precatório"
                              value={data.numeroPrecatorio || numeroPrecatorio}
                            />
                            <InfoField
                              label="Número CNJ"
                              value={jurisData.numero_cnj}
                            />
                            <InfoField
                              label="Polo Ativo"
                              value={jurisData.titulo_polo_ativo}
                            />
                            <InfoField
                              label="Polo Passivo"
                              value={jurisData.titulo_polo_passivo}
                            />
                          </Box>
                          <Box>
                            <InfoField
                              label="Ano Início"
                              value={jurisData.ano_inicio}
                            />
                            <InfoField
                              label="Data Início"
                              value={formatDate(jurisData.data_inicio)}
                            />
                            <InfoField
                              label="Última Movimentação"
                              value={formatDate(jurisData.data_ultima_movimentacao)}
                            />
                            <InfoField
                              label="Qtd. Movimentações"
                              value={jurisData.quantidade_movimentacoes}
                            />
                          </Box>
                        </SimpleGrid>

                        {/* Origem Section */}
                        {(jurisData.estado_origem || jurisData.unidade_origem) && (
                          <Box mt={6}>
                            <Heading size="sm" mb={3}>Origem</Heading>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                              {jurisData.estado_origem && (
                                <Box>
                                  <Text fontSize="sm" color={textColor} fontWeight="medium">Estado</Text>
                                  <HStack mt={1}>
                                    <Badge colorScheme="blue">{jurisData.estado_origem.sigla}</Badge>
                                    <Text>{jurisData.estado_origem.nome}</Text>
                                  </HStack>
                                </Box>
                              )}
                              {jurisData.unidade_origem && (
                                <Box>
                                  <Text fontSize="sm" color={textColor} fontWeight="medium">Unidade</Text>
                                  <Text>{jurisData.unidade_origem.nome}</Text>
                                  {jurisData.unidade_origem.tribunal_sigla && (
                                    <Badge colorScheme="purple" mt={1}>{jurisData.unidade_origem.tribunal_sigla}</Badge>
                                  )}
                                </Box>
                              )}
                            </SimpleGrid>
                          </Box>
                        )}

                        {/* Documentos Section */}
                        {(documentos.cpfs?.length > 0 || documentos.cnpjs?.length > 0) && (
                          <Box mt={6}>
                            <Heading size="sm" mb={3}>Documentos Encontrados</Heading>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                              {documentos.cpfs?.length > 0 && (
                                <Box>
                                  <Text fontSize="sm" color={textColor} fontWeight="medium">CPFs</Text>
                                  <VStack align="start" spacing={1} mt={1}>
                                    {documentos.cpfs.map((cpf, idx) => (
                                      <Badge key={idx} colorScheme="green" variant="outline">
                                        {formatCPF(cpf)}
                                      </Badge>
                                    ))}
                                  </VStack>
                                </Box>
                              )}
                              {documentos.cnpjs?.length > 0 && (
                                <Box>
                                  <Text fontSize="sm" color={textColor} fontWeight="medium">CNPJs</Text>
                                  <VStack align="start" spacing={1} mt={1}>
                                    {documentos.cnpjs.map((cnpj, idx) => (
                                      <Badge key={idx} colorScheme="purple" variant="outline">
                                        {formatCNPJ(cnpj)}
                                      </Badge>
                                    ))}
                                  </VStack>
                                </Box>
                              )}
                            </SimpleGrid>
                          </Box>
                        )}

                        {/* Processos Relacionados Section */}
                        {processosRelacionados.length > 0 && (
                          <Box mt={6}>
                            <Heading size="sm" mb={3}>Processos Relacionados</Heading>
                            <HStack spacing={2} flexWrap="wrap">
                              {processosRelacionados.map((proc, idx) => (
                                <Badge key={idx} colorScheme="orange" variant="outline">
                                  {proc.numero}
                                </Badge>
                              ))}
                            </HStack>
                          </Box>
                        )}

                        {/* Status */}
                        <Box mt={6}>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <Box>
                              <Text fontSize="sm" color={textColor} fontWeight="medium">Fontes Arquivadas</Text>
                              <Badge colorScheme={jurisData.fontes_tribunais_estao_arquivadas ? 'red' : 'green'}>
                                {jurisData.fontes_tribunais_estao_arquivadas ? 'Sim' : 'Não'}
                              </Badge>
                            </Box>
                            <Box>
                              <Text fontSize="sm" color={textColor} fontWeight="medium">Última Verificação</Text>
                              <Text fontSize="sm">{jurisData.tempo_desde_ultima_verificacao || '-'}</Text>
                            </Box>
                          </SimpleGrid>
                        </Box>
                      </CardBody>
                    </Card>
                  </TabPanel>

                  {/* Tab 2: Fontes */}
                  {fontes.length > 0 && (
                    <TabPanel>
                      <Card variant="outline" borderColor={borderColor} bg={cardBg}>
                        <CardHeader pb={2}>
                          <Heading size="md">Fontes do Processo</Heading>
                        </CardHeader>
                        <Divider />
                        <CardBody>
                          <Accordion allowMultiple>
                            {fontes.map((fonte, index) => (
                              <AccordionItem key={fonte.processo_fonte_id || index}>
                                <h2>
                                  <AccordionButton>
                                    <Box flex="1" textAlign="left">
                                      <HStack spacing={4} flexWrap="wrap">
                                        <Badge colorScheme="blue">
                                          {fonte.sigla || fonte.nome}
                                        </Badge>
                                        <Badge colorScheme={fonte.status_predito === 'ATIVO' ? 'green' : 'gray'}>
                                          {fonte.status_predito || 'N/A'}
                                        </Badge>
                                        <Text fontSize="sm" color={textColor}>
                                          {fonte.descricao}
                                          {fonte.envolvidos?.length > 0 && ` - ${fonte.envolvidos.length} envolvidos`}
                                        </Text>
                                      </HStack>
                                    </Box>
                                    <AccordionIcon />
                                  </AccordionButton>
                                </h2>
                                <AccordionPanel pb={4}>
                                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                                    <InfoField label="Tribunal" value={fonte.tribunal?.nome} />
                                    <InfoField label="Grau" value={fonte.grau_formatado} />
                                    <InfoField label="Data Início" value={formatDate(fonte.data_inicio)} />
                                    <InfoField label="Última Movimentação" value={formatDate(fonte.data_ultima_movimentacao)} />
                                    <InfoField label="Qtd. Movimentações" value={fonte.quantidade_movimentacoes} />
                                    <InfoField label="Qtd. Envolvidos" value={fonte.quantidade_envolvidos} />
                                  </SimpleGrid>

                                  {/* Capa info */}
                                  {fonte.capa && (
                                    <Box mt={4}>
                                      <Heading size="sm" mb={3}>Capa do Processo</Heading>
                                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                        {fonte.capa.valor_causa && (
                                          <Box>
                                            <Text fontSize="sm" color={textColor} fontWeight="medium">Valor da Causa</Text>
                                            <Text fontWeight="bold" color="green.500">
                                              {fonte.capa.valor_causa.valor_formatado}
                                            </Text>
                                          </Box>
                                        )}
                                        <InfoField label="Data Distribuição" value={formatDate(fonte.capa.data_distribuicao)} />
                                      </SimpleGrid>

                                      {/* Informações Complementares */}
                                      {fonte.capa.informacoes_complementares?.length > 0 && (
                                        <Box mt={4}>
                                          <Text fontSize="sm" color={textColor} fontWeight="medium" mb={2}>
                                            Informações Complementares
                                          </Text>
                                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                                            {fonte.capa.informacoes_complementares
                                              .filter(info => info.valor !== 'Não informado')
                                              .map((info, idx) => (
                                                <Box key={idx} p={2} bg={tableHeaderBg} borderRadius="md">
                                                  <Text fontSize="xs" color={textColor}>{info.tipo}</Text>
                                                  <Text fontSize="sm" fontWeight="medium">{info.valor}</Text>
                                                </Box>
                                              ))}
                                          </SimpleGrid>
                                        </Box>
                                      )}
                                    </Box>
                                  )}

                                  {/* Envolvidos da fonte */}
                                  {fonte.envolvidos && fonte.envolvidos.length > 0 && (
                                    <Box mt={4}>
                                      <Heading size="sm" mb={3}>Envolvidos nesta Fonte</Heading>
                                      <VStack spacing={3} align="stretch">
                                        {fonte.envolvidos.map((env, idx) => (
                                          <Card key={idx} size="sm" variant="outline">
                                            <CardBody>
                                              <Flex justify="space-between" align="start" mb={2}>
                                                <Box flex="1">
                                                  <HStack spacing={2} mb={1} flexWrap="wrap">
                                                    <Text fontWeight="bold" fontSize="md">
                                                      {env.prefixo && `${env.prefixo} `}{env.nome}
                                                    </Text>
                                                    <Badge
                                                      colorScheme={getPoloColor(env.polo)}
                                                      fontSize="xs"
                                                    >
                                                      {env.polo}
                                                    </Badge>
                                                    {env.tipo_pessoa && (
                                                      <Badge
                                                        colorScheme={env.tipo_pessoa === 'JURIDICA' ? 'purple' : 'orange'}
                                                        fontSize="xs"
                                                      >
                                                        {env.tipo_pessoa}
                                                      </Badge>
                                                    )}
                                                  </HStack>
                                                  {env.tipo && (
                                                    <Text fontSize="sm" color={textColor}>
                                                      {env.tipo} {env.tipo_normalizado && env.tipo !== env.tipo_normalizado && `(${env.tipo_normalizado})`}
                                                    </Text>
                                                  )}
                                                </Box>
                                              </Flex>

                                              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} fontSize="sm">
                                                {/* CPF/CNPJ */}
                                                {(env.cpf || env.cnpj) && (
                                                  <Box>
                                                    <Text color={textColor} fontSize="xs">Documento:</Text>
                                                    <Text fontFamily="mono">
                                                      {env.cpf ? `CPF: ${formatCPF(env.cpf)}` : ''}
                                                      {env.cnpj ? `CNPJ: ${formatCNPJ(env.cnpj)}` : ''}
                                                    </Text>
                                                  </Box>
                                                )}

                                                {/* OABs do envolvido */}
                                                {env.oabs && env.oabs.length > 0 && (
                                                  <Box>
                                                    <Text color={textColor} fontSize="xs">OAB:</Text>
                                                    <HStack spacing={2} flexWrap="wrap">
                                                      {env.oabs.map((oab, oabIdx) => (
                                                        <Badge key={oabIdx} variant="outline" colorScheme="teal">
                                                          {oab.uf} {oab.numero}
                                                        </Badge>
                                                      ))}
                                                    </HStack>
                                                  </Box>
                                                )}

                                                {/* Quantidade de processos */}
                                                {env.quantidade_processos && (
                                                  <Box>
                                                    <Text color={textColor} fontSize="xs">Processos:</Text>
                                                    <Text>{env.quantidade_processos.toLocaleString('pt-BR')}</Text>
                                                  </Box>
                                                )}
                                              </SimpleGrid>
                                            </CardBody>
                                          </Card>
                                        ))}
                                      </VStack>
                                    </Box>
                                  )}
                                </AccordionPanel>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        </CardBody>
                      </Card>
                    </TabPanel>
                  )}

                  {/* Tab 3: Todos Envolvidos */}
                  {allEnvolvidos.length > 0 && (
                    <TabPanel>
                      <Card variant="outline" borderColor={borderColor} bg={cardBg}>
                        <CardHeader pb={2}>
                          <Heading size="md">Todos os Envolvidos</Heading>
                        </CardHeader>
                        <Divider />
                        <CardBody>
                          <TableContainer>
                            <Table variant="simple" size="sm">
                              <Thead>
                                <Tr>
                                  <Th>Nome</Th>
                                  <Th>Tipo</Th>
                                  <Th>Polo</Th>
                                  <Th>Documento</Th>
                                  <Th>Fonte</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {allEnvolvidos.map((env, idx) => (
                                  <Tr key={idx}>
                                    <Td>
                                      <VStack align="start" spacing={0}>
                                        <Text fontWeight="medium">
                                          {env.prefixo && `${env.prefixo} `}{env.nome}
                                        </Text>
                                        <Badge
                                          colorScheme={env.tipo_pessoa === 'JURIDICA' ? 'purple' : 'orange'}
                                          fontSize="xs"
                                        >
                                          {env.tipo_pessoa}
                                        </Badge>
                                      </VStack>
                                    </Td>
                                    <Td>
                                      <Text fontSize="sm">{env.tipo}</Text>
                                    </Td>
                                    <Td>
                                      <Badge colorScheme={getPoloColor(env.polo)}>
                                        {env.polo}
                                      </Badge>
                                    </Td>
                                    <Td>
                                      <Text fontFamily="mono" fontSize="xs">
                                        {env.cpf ? formatCPF(env.cpf) : env.cnpj ? formatCNPJ(env.cnpj) : '-'}
                                      </Text>
                                    </Td>
                                    <Td>
                                      <Badge colorScheme="gray" fontSize="xs">
                                        {env.fonte_nome}
                                      </Badge>
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </TableContainer>
                        </CardBody>
                      </Card>
                    </TabPanel>
                  )}

                  {/* Tab 4: OABs */}
                  {oabs.length > 0 && (
                    <TabPanel>
                      <Card variant="outline" borderColor={borderColor} bg={cardBg}>
                        <CardHeader pb={2}>
                          <Heading size="md">Advogados (OAB)</Heading>
                        </CardHeader>
                        <Divider />
                        <CardBody>
                          <TableContainer>
                            <Table variant="simple" size="sm">
                              <Thead>
                                <Tr>
                                  <Th>Nome</Th>
                                  <Th>OAB</Th>
                                  <Th>UF</Th>
                                  <Th>Tipo</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {oabs.map((oab, idx) => (
                                  <Tr key={oab._id || idx}>
                                    <Td fontWeight="medium">{oab.nome}</Td>
                                    <Td>
                                      <Badge colorScheme="teal" variant="outline">
                                        {oab.numero}
                                      </Badge>
                                    </Td>
                                    <Td>
                                      <Badge colorScheme="blue">{oab.uf}</Badge>
                                    </Td>
                                    <Td>
                                      <Badge colorScheme="purple" variant="outline">
                                        {oab.tipo}
                                      </Badge>
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </TableContainer>
                        </CardBody>
                      </Card>
                    </TabPanel>
                  )}
                </TabPanels>
              </Tabs>
            </Box>
          )}
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default JurisConsultModal;
